import KTData from '../../helpers/data';
import KTComponent from '../component';
import { KTDatepickerCalendar } from './calendar';
import { KTDatepickerStateManager } from './config';
import { KTDatepickerKeyboard } from './keyboard';
import {
	DateRangeInterface,
	KTDatepickerConfigInterface,
	SelectedDateInterface,
} from './types';

export class KTDatepicker extends KTComponent {
	protected override readonly _name: string = 'datepicker';
	protected override readonly _config: KTDatepickerConfigInterface;

	private _state: KTDatepickerStateManager;
	private _calendar: KTDatepickerCalendar;
	private _keyboard: KTDatepickerKeyboard;

	private _dateInputElement: HTMLInputElement;
	private _startDateInputElement: HTMLInputElement;
	private _endDateInputElement: HTMLInputElement;
	private _dateInputDisplayElement: HTMLElement;

	/**
	 * Constructor for the KTDatepicker class.
	 *
	 * @param {HTMLElement} element - The HTML element that the datepicker will be attached to.
	 * @param {KTDatepickerConfigInterface} config - Optional configuration object for the datepicker.
	 * @return {void}
	 */
	constructor(element: HTMLElement, config?: KTDatepickerConfigInterface) {
		super();

		// Check if the element already has a datepicker instance attached to it
		if (KTData.has(element, this._name)) {
			// If it does, return early to avoid attaching a new instance
			return;
		}

		// Initialize the datepicker with the provided element
		this._init(element);

		// Build the configuration object by merging the default config with the provided config
		this._buildConfig(config);

		// Store the instance of the datepicker directly on the element
		(element as any).instance = this;

		this._element.setAttribute('tabindex', '0');

		this._state = new KTDatepickerStateManager(this._config);
		this._config = this._state.getConfig();

		// Get input elements from HTML
		this._getInputElements();

		this._calendar = new KTDatepickerCalendar(this._element, this._state);
		this._keyboard = new KTDatepickerKeyboard(this._element, this._state);

		// Initialize the datepicker UI
		this._initDatePicker();

		this._dispatchEvent(this._config.initEvent);
		this._fireEvent(this._config.initEvent);
	}

	private _getInputElements() {
		this._dateInputElement = this._element.querySelector(
			`input[${this._config.inputSelector}]`
		) as HTMLInputElement;

		if (this._config.range) {
			this._startDateInputElement = this._element.querySelector(
				`input[${this._config.startInputSelector}]`
			) as HTMLInputElement;
			this._endDateInputElement = this._element.querySelector(
				`input[${this._config.endInputSelector}]`
			) as HTMLInputElement;
		}
	}

	private _destroy(): void {
		// Remove data attribute
		KTData.remove(this._element, this._name);

		// Remove instance from static map
		KTDatepicker._instances.delete(this._element);

		// Clear private properties
		this._element = null;
	}

	/**
	 * ========================================================================
	 * Date utils
	 * ========================================================================
	 */

	private _formatDateRange(
		dateRange: DateRangeInterface,
		dateFormat: string,
		timeFormat?: string
	): string {
		return `${this._formatDateTime(
			dateRange.startDate,
			dateFormat,
			timeFormat
		)}${this._config.rangeSeparator}${this._formatDateTime(
			dateRange.endDate,
			dateFormat,
			timeFormat
		)}`;
	}

	private _formatDateTime(
		date: Date,
		dateFormat: string,
		timeFormat?: string
	): string {
		if (!date) {
			return '';
		}

		const formattedDate = this._formatDateOnly(date, dateFormat);

		if (this._config.enableTime && timeFormat) {
			const formattedTime = this._state.formatTime(date, timeFormat);

			return `${formattedDate} ${formattedTime}`;
		}

		return formattedDate;
	}

	private _formatDateOnly(date: Date, dateFormat: string): string {
		if (!date) {
			return '';
		}

		const day = date.getDate();
		const month = date.getMonth() + 1;
		const year = date.getFullYear();

		return dateFormat.replace(/dd|MM|yyyy/g, (match) => {
			switch (match) {
				case 'dd':
					return this._state.padZero(day);
				case 'MM':
					return this._state.padZero(month);
				case 'yyyy':
					return String(year);
				default:
					return match;
			}
		});
	}

	/**
	 * ========================================================================
	 * Date input
	 * ========================================================================
	 */

	private _updateInputValue(selectedDate: SelectedDateInterface): void {
		if (!this._dateInputElement) {
			return;
		}

		const dateFormat = this._state.getDateFormat();
		const timeFormat = this._config.enableTime
			? this._state.getTimeFormat()
			: undefined;

		if (this._config.range && selectedDate.selectedDateRange?.startDate) {
			const { startDate, endDate } = selectedDate.selectedDateRange;

			// Format date range and update main input
			this._dateInputElement.value = this._formatDateRange(
				selectedDate.selectedDateRange,
				dateFormat,
				timeFormat
			);

			// Update hidden inputs (if they exist)
			if (this._startDateInputElement instanceof HTMLInputElement) {
				this._startDateInputElement.value = this._formatDateTime(
					startDate,
					dateFormat,
					timeFormat
				);
			}

			if (this._endDateInputElement instanceof HTMLInputElement) {
				this._endDateInputElement.value = this._formatDateTime(
					endDate,
					dateFormat,
					timeFormat
				);
			}
		} else {
			// Format single date and update main input
			this._dateInputElement.value = this._formatDateTime(
				selectedDate.selectedDate,
				dateFormat,
				timeFormat
			);
		}
	}

	/**
	 * ========================================================================
	 * Listeners
	 * ========================================================================
	 */

	private _attachSelectedDateListener() {
		this._element.addEventListener(
			this._config.dateChangeEvent,
			this._handleOnDateChange.bind(this)
		);
		this._element.addEventListener(
			this._config.keyboardNavigateEvent,
			this._handleOnCalendarUpdate.bind(this)
		);
		this._element.addEventListener(
			this._config.calendarRenderedEvent,
			this._handleOnCalendarUpdate.bind(this)
		);

		if (this._dateInputElement) {
			this._dateInputElement.addEventListener(
				'change',
				this._handleOnDateChange.bind(this)
			);
		}
	}

	private _handleOnDateChange(event: CustomEvent) {
		const selected = (event.detail.payload as SelectedDateInterface) ?? {
			selectedDate: null,
			selectedDateRange: null,
		};

		// TODO handle error on failed parse

		this._updateInputValue(selected);
	}

	private _handleOnCalendarUpdate(_event: CustomEvent) {
		this._initKeyboardSupport();
	}

	/**
	 * ========================================================================
	 * Inputs UI
	 * ========================================================================
	 */

	private _initDatePicker(): void {
		this._initKeyboardSupport();

		if (this._calendar instanceof KTDatepickerCalendar) {
			this._calendar.renderCalendar();
		}

		this._attachSelectedDateListener();

		this._element.classList.add('datepicker-initialized');
	}

	private _initKeyboardSupport(): void {
		this._keyboard.setInputElement(this._dateInputElement);
		const dateInputDisplayElement = this._renderDateInputDisplay(
			this._state.getSelectedDateInterface()
		);
		this._keyboard.setInputDisplayElement(dateInputDisplayElement);
	}

	private _renderDateInputDisplay(
		selectedDate: SelectedDateInterface
	): HTMLElement {
		let newElement: HTMLElement | null = null;

		if (this._config.range && selectedDate.selectedDateRange) {
			const { startDate, endDate } = selectedDate.selectedDateRange;
			const startDateTimeDisplay =
				this._state.getDateDisplayInterface(startDate);
			const endDateTimeDisplay = this._state.getDateDisplayInterface(endDate);
			newElement = this._state
				.getTemplates()
				?.datetimeDisplayRange(
					this._state,
					startDateTimeDisplay,
					endDateTimeDisplay,
					true,
					this._config.enableTime,
					this._config.enableTime,
					this._state.is12Hours()
				);
		} else {
			const dateTimeDisplay = this._state.getDateDisplayInterface(
				selectedDate.selectedDate
			);
			newElement = this._state
				.getTemplates()
				?.datetimeDisplay(
					this._state,
					dateTimeDisplay,
					true,
					this._config.enableTime,
					this._config.enableTime,
					this._state.is12Hours()
				);
		}

		if (this._dateInputDisplayElement) {
			this._dateInputDisplayElement.replaceWith(newElement);
		} else {
			this._element.appendChild(newElement);
		}

		this._dateInputDisplayElement = newElement;

		return newElement;
	}

	/**
	 * ========================================================================
	 * Public API
	 * ========================================================================
	 */

	public getDate(): Date | null | DateRangeInterface {
		if (this._config.range) {
			return this._state.getSelectedDateRange();
		} else {
			return this._state.getSelectedDate();
		}
	}

	public setDate(date: Date | null): void {
		if (this._config.range) {
			this._state.setSelectedDateRange(date as Date, null);
		} else {
			this._state.setSelectedDate(date);
		}
	}

	public getDateRange(): DateRangeInterface | null {
		return this._state.getSelectedDateRange();
	}

	public setDateRange(start: Date | null, end: Date | null): void {
		this._state.setSelectedDateRange(start, end);
	}

	public setMinDate(minDate: Date | null): void {
		this._config.minDate = minDate;
	}

	public setMaxDate(maxDate: Date | null): void {
		this._config.maxDate = maxDate;
	}

	public update(): void {
		if (this._calendar instanceof KTDatepickerCalendar) {
			this._calendar.renderCalendar();
		}
	}

	public destroy(): void {
		this._destroy();
	}

	/**
	 * ========================================================================
	 * Static instances
	 * ========================================================================
	 */

	private static readonly _instances = new Map<HTMLElement, KTDatepicker>();

	public static createInstances(): void {
		const elements = document.querySelectorAll<HTMLElement>(
			'[data-kt-datepicker]'
		);

		elements.forEach((element) => {
			if (
				element.hasAttribute('data-kt-datepicker') &&
				!element.classList.contains('datepicker-initialized')
			) {
				const instance = new KTDatepicker(element);
				this._instances.set(element, instance);
			}
		});
	}

	public static init(): void {
		KTDatepicker.createInstances();
	}
}

if (typeof window !== 'undefined') {
	window.KTDatepicker = KTDatepicker;
}
